import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../config/theme';

const CardContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.md};
  background: ${theme.colors.primary.background};
  border: 1px solid ${theme.colors.neutral.light};
  border-radius: 8px;
  transition: ${theme.transitions.default};
  
  &:hover {
    border-color: ${theme.colors.neutral.medium};
  }
`;

const CardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const CardBrand = styled.img`
  height: 32px;
  width: auto;
  object-fit: contain;
`;

const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const CardNumber = styled.span`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  font-weight: 500;
  color: ${theme.colors.primary.text};
`;

const CardMeta = styled.span`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.875rem;
  color: ${theme.colors.neutral.medium};
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const ActionButton = styled.button`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.primary.buttons};
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  transition: ${theme.transitions.default};
  
  &:hover {
    opacity: 0.8;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DefaultBadge = styled.span`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.primary.background};
  background: #4CAF50;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: 12px;
  margin-left: ${theme.spacing.sm};
`;

const DeleteConfirmation = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.875rem;
  color: #f44336;
`;

const ConfirmButton = styled(ActionButton)`
  color: #f44336;
`;

const CancelButton = styled(ActionButton)`
  color: ${theme.colors.neutral.medium};
`;

const PaymentMethodCard = ({ 
  paymentMethod, 
  onDelete, 
  onSetDefault,
  isDeleting = false,
  brandLogos = {}
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(paymentMethod.id);
      setShowDeleteConfirm(false);
    }
  };

  const handleSetDefault = async () => {
    if (onSetDefault && !paymentMethod.is_default) {
      await onSetDefault(paymentMethod.id);
    }
  };

  const formatExpiry = () => {
    const month = String(paymentMethod.exp_month).padStart(2, '0');
    const year = String(paymentMethod.exp_year).slice(-2);
    return `${month}/${year}`;
  };

  const getBrandLogo = () => {
    const brand = paymentMethod.brand?.toLowerCase();
    return brandLogos[brand] || brandLogos.generic || null;
  };

  return (
    <CardContainer>
      <CardInfo>
        {getBrandLogo() && (
          <CardBrand 
            src={getBrandLogo()} 
            alt={paymentMethod.brand} 
          />
        )}
        
        <CardDetails>
          <CardNumber>
            •••• {paymentMethod.last4}
            {paymentMethod.is_default && (
              <DefaultBadge>Default</DefaultBadge>
            )}
          </CardNumber>
          
          <CardMeta>
            {paymentMethod.holder_name && `${paymentMethod.holder_name} • `}
            Expires {formatExpiry()}
          </CardMeta>
        </CardDetails>
      </CardInfo>

      <CardActions>
        {showDeleteConfirm ? (
          <DeleteConfirmation>
            <span>Delete this card?</span>
            <ConfirmButton
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Yes'}
            </ConfirmButton>
            <CancelButton
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              No
            </CancelButton>
          </DeleteConfirmation>
        ) : (
          <>
            {!paymentMethod.is_default && onSetDefault && (
              <ActionButton onClick={handleSetDefault}>
                Set as Default
              </ActionButton>
            )}
            
            {onDelete && (
              <ActionButton
                onClick={() => setShowDeleteConfirm(true)}
                style={{ color: theme.colors.accent.danger }}
              >
                Delete
              </ActionButton>
            )}
          </>
        )}
      </CardActions>
    </CardContainer>
  );
};

export default PaymentMethodCard;